import { useEffect, useRef } from "react";

interface UseNotificationSoundOptions {
  soundUrl?: string;
  volume?: number;
  isEnabled?: boolean;
}

export function useNotificationSound({
  soundUrl = "/sounds/notification.mp3",
  volume = 0.5,
  isEnabled = true,
}: UseNotificationSoundOptions = {}) {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const enabledRef = useRef<boolean>(isEnabled);
  const soundUrlRef = useRef<string>(soundUrl);

  // Get user preferred sound from localStorage if available
  useEffect(() => {
    const savedSoundPath = localStorage.getItem("snudbud_notification_sound_path");
    if (savedSoundPath) {
      soundUrlRef.current = savedSoundPath;
    }
  }, []);

  useEffect(() => {
    // Create audio element with current sound URL
    audioRef.current = new Audio(soundUrlRef.current);
    audioRef.current.volume = volume;

    return () => {
      // Clean up
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, [volume]);

  // Update sound URL if it changes in localStorage
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === "snudbud_notification_sound_path" && e.newValue) {
        soundUrlRef.current = e.newValue;
        if (audioRef.current) {
          audioRef.current.src = e.newValue;
        }
      }
    };

    window.addEventListener("storage", handleStorageChange);
    return () => {
      window.removeEventListener("storage", handleStorageChange);
    };
  }, []);

  // Update enabled state when the prop changes
  useEffect(() => {
    enabledRef.current = isEnabled;
  }, [isEnabled]);

  const play = () => {
    if (audioRef.current && enabledRef.current) {
      // Check if user selected sound has changed
      const currentSoundPath = localStorage.getItem("snudbud_notification_sound_path");
      if (currentSoundPath && currentSoundPath !== audioRef.current.src) {
        audioRef.current.src = currentSoundPath;
        soundUrlRef.current = currentSoundPath;
      }

      // Reset to beginning if already playing
      audioRef.current.currentTime = 0;
      audioRef.current.play().catch((err) => {
        // This typically happens if browser has restrictions on autoplay
        console.warn("Could not play notification sound:", err);
      });
    }
  };

  const setEnabled = (enabled: boolean) => {
    enabledRef.current = enabled;
  };

  return { play, setEnabled };
}
